/**
 * Таблица прав «разделы × должности» — D232, §8.7 ТЗ платформы.
 *
 * Строки — разделы трёх агентов, сгруппированные по агентам; столбцы —
 * должности; в клетке две кнопки, «видеть» и «править». Нажатие меняет
 * право должности сразу. Так владелец просил с 12.09.2026 («как в iiko»):
 * до этого должность настраивалась по одной, и сравнить две было нечем.
 *
 * Два правила сделаны за человека, а не оставлены ему:
 *
 * - «править» без «видеть» не бывает: включили «править» — «видеть»
 *   включается тоже; выключили «видеть» — «править» выключается тоже;
 * - денежные строки у должностей закрыты замком (§8.4): деньги выдаются
 *   человеку в его карточке. Финансы целиком денежные — вход в финансы
 *   выдаётся поимённо и сразу открывает все их разделы (D230).
 *
 * На телефоне таблица прокручивается вбок внутри себя, первый столбец с
 * названиями разделов стоит на месте.
 */

import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { ApiError } from '@shell/api';
import { Notice } from '../../ui';
import { SERVICE_NAMES, SERVICE_ORDER, problemText, teamApi } from './api';
import type { PositionRights, RightObject, RightsScreen } from './api';

type Change = {
  positionId: number;
  service: string;
  object: string;
  action: string;
  granted: boolean;
};

type Result = { ok: boolean; text: string } | null;

const VIEW = 'view';
const EDIT = 'edit';

/** Какие изменения нужны, чтобы «править» не жило без «видеть». */
export function changesFor(
  object: RightObject,
  action: string,
  granted: boolean,
  has: (action: string) => boolean,
): { action: string; granted: boolean }[] {
  const steps = [{ action, granted }];
  if (action === EDIT && granted && object.actions.includes(VIEW) && !has(VIEW)) {
    steps.unshift({ action: VIEW, granted: true });
  }
  if (action === VIEW && !granted && object.actions.includes(EDIT) && has(EDIT)) {
    steps.unshift({ action: EDIT, granted: false });
  }
  return steps;
}

export function RightsMatrix({ screen, mayEdit }: { screen: RightsScreen; mayEdit: boolean }) {
  const client = useQueryClient();
  const [busy, setBusy] = useState<string | null>(null);
  const [result, setResult] = useState<Result>(null);
  const change = useMutation<unknown, ApiError, Change[]>({
    mutationFn: async (changes) => {
      for (const { positionId, ...body } of changes) {
        await teamApi.setPositionRight(positionId, body);
      }
    },
    onSuccess: (_data, changes) => {
      const position = screen.positions.find((item) => item.id === changes[0]?.positionId);
      setResult({
        ok: true,
        text: `Сохранено. Права получат все сотрудники должности «${position?.name ?? ''}».`,
      });
    },
    onError: (error) => setResult({ ok: false, text: problemText(error) }),
    onSettled: () => {
      setBusy(null);
      void client.invalidateQueries({ queryKey: ['team'] });
    },
  });

  const { positions, objects } = screen;
  if (positions.length === 0) {
    return <p className="team-faint">Должностей пока нет — заведите первую выше.</p>;
  }

  const toggle = (position: PositionRights, object: RightObject, action: string) => {
    const granted = new Set(position.rights[object.service]?.granted ?? []);
    const has = (a: string) => granted.has(`${object.code}:${a}`);
    const steps = changesFor(object, action, !has(action), has);
    setResult(null);
    setBusy(`${position.id}:${object.service}:${object.code}`);
    change.mutate(
      steps.map((step) => ({
        positionId: position.id,
        service: object.service,
        object: object.code,
        ...step,
      })),
    );
  };

  return (
    <section className="team-block team-block--card rights-matrix" aria-label="Таблица прав">
      <h3 className="block-title">Права должностей</h3>
      <p className="team-hint">
        <span className="rights-legend rights-legend--on">В</span> — видеть раздел,{' '}
        <span className="rights-legend rights-legend--on">П</span> — править его. Нажатие меняет
        право сразу у всех сотрудников должности. 🔒 — выдаётся только человеку, в его карточке.
      </p>
      {result && <Notice kind={result.ok ? 'ok' : 'error'}>{result.text}</Notice>}
      <div className="rights-matrix__scroll">
        <table className="rights-matrix__table">
          <thead>
            <tr>
              <th scope="col" className="rights-matrix__corner">
                Раздел
              </th>
              {positions.map((position) => (
                <th key={position.id} scope="col" className="rights-matrix__position">
                  {position.name}
                  {position.is_owner && <span className="rights-matrix__all">все права</span>}
                </th>
              ))}
            </tr>
          </thead>
          {SERVICE_ORDER.map((service) => {
            const rows = objects.filter((item) => item.service === service);
            if (rows.length === 0) return null;
            return (
              <tbody key={service}>
                <tr className="rights-matrix__group">
                  <th scope="rowgroup" colSpan={positions.length + 1}>
                    {SERVICE_NAMES[service] ?? service}
                    {service === 'finance' && (
                      <span className="rights-matrix__group-hint">
                        вход в финансы выдаётся поимённо и открывает все разделы; лишнее снимается
                        в карточке человека
                      </span>
                    )}
                  </th>
                </tr>
                {rows.map((object) => (
                  <tr key={object.code}>
                    <th scope="row" className="rights-matrix__object">
                      <span className="rights-matrix__title">{object.title}</span>
                      <span className="rights-matrix__hint">{object.hint}</span>
                    </th>
                    {positions.map((position) => (
                      <td key={position.id} className="rights-matrix__cell">
                        <Cell
                          position={position}
                          object={object}
                          disabled={!mayEdit || busy !== null}
                          pending={busy === `${position.id}:${object.service}:${object.code}`}
                          onToggle={(action) => toggle(position, object, action)}
                        />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            );
          })}
        </table>
      </div>
    </section>
  );
}

function Cell({
  position,
  object,
  disabled,
  pending,
  onToggle,
}: {
  position: PositionRights;
  object: RightObject;
  disabled: boolean;
  pending: boolean;
  onToggle: (action: string) => void;
}) {
  if (object.is_money && !position.is_owner) {
    return (
      <span className="rights-lock" title="Выдаётся только человеку, в его карточке">
        🔒
      </span>
    );
  }
  const granted = new Set(position.rights[object.service]?.granted ?? []);
  return (
    <span className={`rights-cell${pending ? ' rights-cell--pending' : ''}`}>
      {[VIEW, EDIT].map((action) => {
        if (!object.actions.includes(action)) {
          return <span key={action} className="rights-toggle rights-toggle--none" aria-hidden />;
        }
        const on = position.is_owner || granted.has(`${object.code}:${action}`);
        const word = action === VIEW ? 'видеть' : 'править';
        return (
          <button
            key={action}
            type="button"
            className={`rights-toggle${on ? ' rights-toggle--on' : ''}`}
            aria-pressed={on}
            aria-label={`${position.name}: ${object.title} — ${word}`}
            title={`${word}: ${on ? 'да' : 'нет'}`}
            disabled={disabled || position.is_owner}
            onClick={() => onToggle(action)}
          >
            {action === VIEW ? 'В' : 'П'}
          </button>
        );
      })}
    </span>
  );
}
